import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Pretive from '../media/Preative.svg';
import Gyanbeej from '../media/LOGO Gyanbeej.png';
import GyanbeejOnDark from '../media/LOGO Gyanbeej transparent.png';
import GyanSparkLogoLight from '../media/gyanspark-logo-light-horizontal.svg';
import GyanSparkLogoDark from '../media/gyanspark-logo-dark-horizontal.svg';

const PRODUCTS = [
  {
    to: '/products/gyanspark',
    logo: GyanSparkLogoLight,
    logoOnDark: GyanSparkLogoDark,
    name: 'GyanSpark',
    blurb: 'Personalization layer for EdTech platforms',
  },
  {
    to: '/products/gyanbeej',
    logo: Gyanbeej,
    logoOnDark: GyanbeejOnDark,
    name: 'Gyanbeej',
    blurb: 'AI learning assistant for NCERT students, Class 6–10',
  },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [dropdownOffset, setDropdownOffset] = useState(0);
  const productsRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    setIsMenuOpen(false);
    setIsProductsOpen(false);
  }, [pathname]);

  useLayoutEffect(() => {
    if (!isProductsOpen || !productsRef.current) return;

    const updateOffset = () => {
      const button = productsRef.current;
      if (!button) return;
      const buttonRect = button.getBoundingClientRect();
      const dropdownWidth = Math.min(416, window.innerWidth * 0.9);
      const margin = 16;

      let offset = 0;
      const rightEdge = buttonRect.left + offset + dropdownWidth;
      if (rightEdge > window.innerWidth - margin) {
        offset = window.innerWidth - margin - dropdownWidth - buttonRect.left;
      }
      const leftEdge = buttonRect.left + offset;
      if (leftEdge < margin) {
        offset = margin - buttonRect.left;
      }

      setDropdownOffset(offset);
    };

    updateOffset();
    window.addEventListener('resize', updateOffset);
    return () => window.removeEventListener('resize', updateOffset);
  }, [isProductsOpen]);

  const linkClass = ({ isActive }) =>
    `text-sm font-semibold tracking-wide transition-colors ${
      isActive ? 'text-purple-400' : 'text-gray-200 hover:text-purple-400'
    }`;

  const isProductsActive = pathname.startsWith('/products');

  return (
    <header className="bg-[#0d0018]/95 backdrop-blur-md text-white fixed w-full z-50 border-b border-white/10">
      <div className="container mx-auto flex justify-between items-center px-[5%] py-3">
        <Link to="/" aria-label="Preative Solutions home">
          <img src={Pretive} alt="Preative Solutions logo" className="h-12 md:h-14" />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/about" className={linkClass}>
            About
          </NavLink>

          <div className="relative" onMouseLeave={() => setIsProductsOpen(false)}>
            <button
              ref={productsRef}
              onMouseEnter={() => setIsProductsOpen(true)}
              onClick={() => setIsProductsOpen(!isProductsOpen)}
              aria-expanded={isProductsOpen}
              className={`flex items-center gap-1.5 text-sm font-semibold tracking-wide transition-colors ${
                isProductsActive ? 'text-purple-400' : 'text-gray-200 hover:text-purple-400'
              }`}
            >
              Products
              <span className={`text-[10px] transition-transform ${isProductsOpen ? 'rotate-180' : ''}`}>▼</span>
            </button>
            {isProductsOpen && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.15 }}
                style={{ left: dropdownOffset }}
                className="absolute top-full pt-4 w-[26rem] max-w-[90vw]"
              >
                <div className="bg-[#150a24] rounded-2xl shadow-2xl border border-white/10 p-2 overflow-hidden">
                  {PRODUCTS.map((p) => (
                    <Link
                      key={p.to}
                      to={p.to}
                      className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/[0.06] transition-colors"
                    >
                      <img src={p.logoOnDark || p.logo} alt="" className="h-6 mt-0.5 object-contain flex-shrink-0" />
                      <span>
                        <span className="block font-bold text-white text-sm">{p.name}</span>
                        <span className="block text-xs text-gray-500 mt-0.5">{p.blurb}</span>
                      </span>
                    </Link>
                  ))}
                  <Link
                    to="/products"
                    className="block px-3 py-2.5 mt-1 text-xs font-semibold text-purple-400 hover:text-purple-300 border-t border-white/10"
                  >
                    Compare both products →
                  </Link>
                </div>
              </motion.div>
            )}
          </div>

          <NavLink to="/careers" className={linkClass}>
            Careers
          </NavLink>
          <Link
            to="/contact"
            className="px-5 py-2 rounded-full bg-white text-[#1a0033] text-sm font-bold hover:bg-purple-500/10 transition-colors"
          >
            Contact
          </Link>
        </nav>

        <button
          className="md:hidden text-white p-2 text-xl leading-none"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle navigation menu"
        >
          {isMenuOpen ? '✕' : '☰'}
        </button>
      </div>

      {isMenuOpen && (
        <motion.nav initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} className="md:hidden border-t border-white/10 pb-4">
          <NavLink to="/about" className="block px-6 py-3 font-medium hover:bg-white/5">
            About
          </NavLink>
          <div className="px-6 pt-3 pb-1 text-xs font-bold uppercase tracking-widest text-gray-500">Products</div>
          {PRODUCTS.map((p) => (
            <NavLink key={p.to} to={p.to} className="flex items-center gap-3 px-6 py-3 hover:bg-white/5">
              <img src={p.logoOnDark || p.logo} alt="" className="h-5 object-contain" />
              <span className="font-medium">{p.name}</span>
            </NavLink>
          ))}
          <NavLink to="/products" className="block px-6 py-2 text-sm text-purple-400">
            Compare both →
          </NavLink>
          <NavLink to="/careers" className="block px-6 py-3 font-medium hover:bg-white/5 border-t border-white/10 mt-2">
            Careers
          </NavLink>
          <NavLink to="/contact" className="block px-6 py-3 font-medium hover:bg-white/5">
            Contact
          </NavLink>
        </motion.nav>
      )}
    </header>
  );
};

export default Header;
